import React, { useContext, useEffect, useRef, useState } from "react";
import { Avatar, Collapse, notification, Popconfirm, Tooltip } from "antd";
import { CloseCircleFilled, DownOutlined, LeftOutlined, TeamOutlined } from "@ant-design/icons";
import UserService from "@Services/UserService";
import { MainContext } from "@Context/provider";
import { SkeletonRows } from "@Components/shared/SkeletonRows";
import InspectorModuleTitle from "@Components/groups/InspectorModuleTitle";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GroupContext, QueryKeys, SystemContext } from "@Constants";
import InspectorInfoField from "@Components/groups/InspectorInfoField";
import UserGroupService from "@Services/UserGroupService";
import { IUserGroupDetails } from "@Models/IUserGroupDetails";
import GroupsListTag from "@Components/groups/GroupsListTag";
import { getRoleUserTag } from "@Components/groups/TagProperties";
import { IRoleAssignmentPaged } from "@Models/IRoleDetails";
import { getSystemId, hasCapability, prettifyName } from "@Utils";
import RoleMenu from "@Components/groups/RoleMenu";
import GroupMenu from "@Components/groups/GroupMenu";
import SessionManager from "@Core/session";
import { ICheckCapFull } from "./types";
import { QueryKeys as GroupsQueryKeys } from "@Features/groups/constants";

const { Panel } = Collapse;

interface IProps {
    instance: string;
    checkCap: ICheckCapFull;
}

const ModuleUserGroups: React.FC<IProps> = ({ instance }: IProps) => {
    const { ui: uiState } = useContext(MainContext);
    const queryClient = useQueryClient();
    const [data, setData] = useState<IUserGroupDetails[]>(null);
    const [fetching, setFetching] = useState<boolean>(true);
    const [updating, setUpdating] = useState<boolean>(false);
    const stateRef = useRef<IUserGroupDetails[]>();
    stateRef.current = data;

    const fetch = async () => {
        const service = new UserGroupService();
        const result = await service.getUser(uiState.currentUser.instance, true);
        if (!result.hasErrors) {
            setData(result.value);
            setFetching(false);
            stateRef.current = result.value;
            return result.value;
        }
        return [];
    };

    useQuery(
        [QueryKeys.UserMemberships],
        async () => {
            return await fetch();
        },
        {
            staleTime: Infinity,
            refetchOnWindowFocus: false,
            enabled: !!SessionManager.getServiceUser(),
        }
    );

    /**
     * Add member to group.
     */
    const groupAddUser = useMutation(async (params: { groupId: string; userId: string }) => {
        setUpdating(true);
        const service = new UserGroupService();
        const res = await service.addMembers(params.groupId, [params.userId]);
        setUpdating(false);
        if (!res.hasErrors) {
            await queryClient.invalidateQueries([QueryKeys.UserGroups]);
            await queryClient.invalidateQueries([GroupsQueryKeys.GroupUsers]);
            await queryClient.invalidateQueries([QueryKeys.GroupMembers, params.groupId]);
            await queryClient.invalidateQueries([QueryKeys.GroupUserRoles, params.groupId]);
            await queryClient.invalidateQueries([QueryKeys.UserMemberships]);

            notification.success({ message: "Added to group." });
        } else {
            notification.warning({ message: "Already added." });
        }
    });

    /**
     * Remove member from group.
     */
    const groupRemoveUser = useMutation(async (params: { groupId: string; userId: string }) => {
        setUpdating(true);
        const service = new UserGroupService();
        const res = await service.removeMembers([params.userId], params.groupId);
        setUpdating(false);
        if (!res.hasErrors) {
            await queryClient.invalidateQueries([QueryKeys.UserGroups]);
            await queryClient.invalidateQueries([QueryKeys.GroupMembers, params.groupId]);
            await queryClient.invalidateQueries([QueryKeys.GroupUserRoles, params.groupId]);
            await queryClient.invalidateQueries([QueryKeys.UserMemberships]);
            notification.success({ message: "Removed from group." });
        } else {
            notification.warning({ message: "Error removing user." });
        }
    });

    /**
     * Create role assignment.
     */
    const roleAddUser = useMutation(
        async (params: { userId: string; roleId: string; groupId: string }) => {
            setUpdating(true);
            const service = new UserService();
            const res = await service.assignRoles([params.userId], params.roleId, params.groupId);
            setUpdating(false);
        },
        {
            onSuccess: async (data, variables) => {
                notification.success({ message: "Role added." });
                await queryClient.invalidateQueries([QueryKeys.UserGroups]);
                await queryClient.invalidateQueries([QueryKeys.GroupUserRoles, variables.groupId]);
                await queryClient.invalidateQueries([QueryKeys.UserMemberships]);
            },
            onError: (error) => {
                notification.warning({ message: "Error adding role." });
            },
        }
    );

    /**
     * Remove role assignment.
     */
    const roleRemoveUser = useMutation(
        async (params: { userId: string; roleId: string; groupId: string }) => {
            setUpdating(true);
            const service = new UserService();
            await service.removeRoles([params.userId], params.roleId, params.groupId);
            setUpdating(false);
        },
        {
            onSuccess: async (data, variables) => {
                notification.success({ message: "Role removed." });
                await queryClient.invalidateQueries([QueryKeys.UserGroups]);
                await queryClient.invalidateQueries([QueryKeys.GroupUserRoles, variables.groupId]);
                await queryClient.invalidateQueries([QueryKeys.UserMemberships]);
            },
            onError: (error) => {
                notification.warning({ message: "Error removing role." });
            },
        }
    );

    const addGroup = (groupId: string) => {
        groupAddUser.mutate({
            userId: instance,
            groupId: groupId,
        });
    };

    const removeGroup = (groupId: string) => {
        groupRemoveUser.mutate({
            userId: instance,
            groupId: groupId,
        });
    };

    const addRole = (roleId: string, groupId: string) => {
        roleAddUser.mutate({
            userId: instance,
            roleId: roleId,
            groupId: groupId,
        });
    };

    const removeRole = (role: IRoleAssignmentPaged) => {
        roleRemoveUser.mutate({
            userId: instance,
            roleId: role.roleId,
            groupId: role.instanceIds[0].id,
        });
    };

    const allowRemove = (groupId: string) => {
        return hasCapability("UserGroup/RemoveMembers", GroupContext, groupId);
    };

    const renderGroupEntry = (group: IUserGroupDetails) => {
        if (group !== null && group.parentGroupId === getSystemId()) {
            return (
                <tr>
                    <td>
                        <InspectorInfoField color={group.groupId === getSystemId() ? "yellow" : "blue"}>
                            <table>
                                <tr>
                                    <td style={{ paddingRight: "10px", width: "140px" }}>{prettifyName(group.name)}</td>
                                    <td>
                                        {group.roles?.map((x, i) => {
                                            const role: IRoleAssignmentPaged = {
                                                userId: instance,
                                                roleId: x.roleId,
                                                roleAssignmentId: x.roleAssignmentId,
                                                contextId:
                                                    group.groupId === getSystemId() ? SystemContext : GroupContext,
                                                instanceIds: [{ id: group.groupId }],
                                                page: 1,
                                            };
                                            return (
                                                <GroupsListTag
                                                    index={i}
                                                    tag={getRoleUserTag(group.groupId, role, () => removeRole(role))}
                                                />
                                            );
                                        })}
                                    </td>
                                    <td>
                                        <RoleMenu
                                            userId={instance}
                                            groupId={group.groupId}
                                            addAction={addRole}
                                            systemRolesOnly={group.groupId === getSystemId()}
                                            roleAssignments={group.roles}
                                        />
                                    </td>
                                </tr>
                            </table>
                        </InspectorInfoField>
                    </td>
                    <td>
                        {group.groupId !== getSystemId() && allowRemove(group.groupId) ? (
                            <Tooltip title="Remove from group">
                                <Popconfirm
                                    title={"Remove user from group and all child groups?"}
                                    onConfirm={() => removeGroup(group.groupId)}
                                    okText="Yes"
                                    cancelText="No"
                                    cancelButtonProps={{ className: "secondary" }}
                                >
                                    <button style={{ paddingLeft: "5px" }} className={"no-style"}>
                                        <CloseCircleFilled />
                                    </button>
                                </Popconfirm>
                            </Tooltip>
                        ) : null}
                    </td>
                </tr>
            );
        }
        return <></>;
    };

    /**
     * Fetch current user groups data.
     */
    useEffect(() => {
        setData(null);
        queryClient.setQueryData([QueryKeys.UserMemberships], fetch()).then();
    }, [uiState.currentUser]);

    return (
        <>
            <Collapse
                destroyInactivePanel={false}
                bordered={false}
                ghost
                expandIconPosition={"end"}
                expandIcon={({ isActive }) => (
                    <Avatar
                        size="small"
                        className={"collapse-icon"}
                        icon={isActive ? <DownOutlined /> : <LeftOutlined />}
                    />
                )}
            >
                <Panel
                    header={
                        <InspectorModuleTitle
                            title={"Groups"}
                            slug={"groups"}
                            fetching={fetching || updating}
                            icon={<TeamOutlined />}
                        />
                    }
                    key={1}
                >
                    {fetching ? (
                        <SkeletonRows rowHeight={32} count={3} />
                    ) : (
                        <table className="inspector-table" style={{ width: "100%" }}>
                            {stateRef.current?.map((x) => {
                                return renderGroupEntry(x);
                            })}
                            <tr>
                                <td style={{ paddingTop: "5px" }}>
                                    <GroupMenu addAction={addGroup} />
                                </td>
                                <td />
                                <td />
                            </tr>
                        </table>
                    )}
                </Panel>
            </Collapse>
            <hr />
        </>
    );
};
export default ModuleUserGroups;
